import React, { useCallback, useEffect, useState } from "react";
import { useAppStore, formatGestureName } from "../store/useAppStore";
import Pagination from "./Pagination";

const SEARCH_PAGE_SIZE = 10;
const RANDOM_POOL_PAGE_SIZE = 48;

const sentenceKey = (sentence) => String(sentence?.documentId || sentence?.id || "");

const isUntranslated = (sentence) => !Array.isArray(sentence?.translations) || sentence.translations.length === 0;

const normalizeRelationArray = (value) => {
  if (Array.isArray(value)) return value.filter(Boolean);
  if (Array.isArray(value?.data)) {
    return value.data
      .map((item) => (item?.attributes ? { id: item.id, ...item.attributes } : item))
      .filter(Boolean);
  }
  if (value?.data?.attributes) {
    return [{ id: value.data.id, ...value.data.attributes }];
  }
  if (value && typeof value === "object") return [value];
  return [];
};

const normalizeMediaFiles = (value) => {
  return normalizeRelationArray(value)
    .map((item) => (item?.attributes ? { id: item.id, ...item.attributes } : item))
    .filter((item) => item?.url);
};

const normalizeMediaUrl = (url, apiBase) => {
  if (!url) return "";
  if (/^https?:\/\//i.test(url)) return url;
  return `${String(apiBase || "").replace(/\/$/, "")}${url}`;
};

const buildSequencePlaybackEntries = (sequenceArr, videosMap, apiBaseStr) => {
  const entries = [];
  (sequenceArr || []).forEach((item, signIndex) => {
    normalizeRelationArray(item?.sign_video_refs).forEach((ref) => {
      const vid = Number(ref?.id);
      if (!Number.isFinite(vid)) return;
      const full = videosMap[vid] || videosMap[String(vid)];
      if (!full) return;
      const files = normalizeMediaFiles(full?.video);
      const mediaFile = files[files.length - 1];
      const url = normalizeMediaUrl(mediaFile?.url, apiBaseStr);
      if (!url) return;
      entries.push({
        url,
        mime: mediaFile?.mime || "video/mp4",
        signLabel: item.label || `Шаг ${signIndex + 1}`,
      });
    });
  });
  return entries;
};

const collectSignVideoRefIds = (sign) =>
  normalizeRelationArray(sign?.sign_videos)
    .map((ref) => Number(ref?.id))
    .filter((id) => Number.isFinite(id));

const pickSignPlaybackFromSign = (sign, videosById, apiBaseStr) => {
  const refs = normalizeRelationArray(sign?.sign_videos);
  for (const ref of refs) {
    const vid = Number(ref?.id);
    if (!Number.isFinite(vid)) continue;
    const full = videosById[vid] || videosById[String(vid)] || ref;
    const files = normalizeMediaFiles(full?.video);
    const file = files[files.length - 1];
    const url = normalizeMediaUrl(file?.url, apiBaseStr);
    if (url) return { url, mime: file?.mime || "video/mp4" };
  }
  return null;
};

const GestureVideoPreview = ({ sign, videosById, apiBase }) => {
  const pb = pickSignPlaybackFromSign(sign, videosById || {}, apiBase);

  if (pb?.url) {
    return (
      <div className="translation-gesture-video">
        <video
          className="translation-sign-video-player"
          controls
          preload="metadata"
          playsInline
          aria-label={`Видео жеста ${sign?.name || sign?.id || ""}`}
        >
          <source src={pb.url} type={pb.mime || "video/mp4"} />
          Ваш браузер не поддерживает воспроизведение видео.
        </video>
      </div>
    );
  }

  return (
    <div className="translation-gesture-video translation-gesture-video--empty">
      <span><i className="fa-regular fa-circle-xmark" aria-hidden="true"></i> Нет видео</span>
    </div>
  );
};

const normalizeSentenceLocal = (item) => {
  if (!item) return null;
  const source = item.attributes ? { id: item.id, ...item.attributes } : item;
  return {
    id: source.id,
    documentId: source.documentId || null,
    text: source.text || "",
    language: source.language || "",
    view: source.view ?? null,
    translations: Array.isArray(source?.translations?.data)
      ? source.translations.data
          .map((relation) => (relation?.attributes ? { id: relation.id, ...relation.attributes } : relation))
          .filter(Boolean)
      : Array.isArray(source?.translations)
        ? source.translations.filter(Boolean)
        : [],
    createdAt: source.createdAt || null,
    updatedAt: source.updatedAt || null,
  };
};

const TranslationPage = () => {
  const apiBase = useAppStore((state) => state.API);
  const token = useAppStore((state) => state.token);
  const signs = useAppStore((state) => state.signs);
  const signsLoading = useAppStore((state) => state.signsLoading);
  const signsError = useAppStore((state) => state.signsError);
  const signsPagination = useAppStore((state) => state.signsPagination);
  const fetchSigns = useAppStore((state) => state.fetchSigns);
  const signVideosById = useAppStore((state) => state.signVideosById);
  const fetchSignVideosByIds = useAppStore((state) => state.fetchSignVideosByIds);
  const createTranslation = useAppStore((state) => state.createTranslation);
  const translationCreateLoading = useAppStore((state) => state.translationCreateLoading);
  const translationCreateError = useAppStore((state) => state.translationCreateError);

  const [sentenceMode, setSentenceMode] = useState("random");
  const [sentenceSearchInput, setSentenceSearchInput] = useState("");
  const [sentenceSearchDebounced, setSentenceSearchDebounced] = useState("");
  const [sentenceOpLoading, setSentenceOpLoading] = useState(false);
  const [sentenceOpError, setSentenceOpError] = useState("");
  const [randomPreview, setRandomPreview] = useState(null);
  const [searchResults, setSearchResults] = useState([]);

  const [selectedSentence, setSelectedSentence] = useState(null);

  const [sequence, setSequence] = useState([]);

  const [signPage, setSignPage] = useState(1);
  const [signPageSize] = useState(12);
  const [signSearchInput, setSignSearchInput] = useState("");
  const [signSearchTerm, setSignSearchTerm] = useState("");
  const [signSearchField, setSignSearchField] = useState("name");

  const [language, setLanguage] = useState("ru_RSL");
  const [notes, setNotes] = useState("");
  const [reviewed, setReviewed] = useState(false);

  const [saveOkMessage, setSaveOkMessage] = useState("");

  const [sequencePreview, setSequencePreview] = useState(null);
  const [sequencePreviewLoading, setSequencePreviewLoading] = useState(false);
  const [sequencePreviewError, setSequencePreviewError] = useState("");

  useEffect(() => {
    const t = setTimeout(() => {
      setSentenceSearchDebounced(sentenceSearchInput.trim());
    }, 350);
    return () => clearTimeout(t);
  }, [sentenceSearchInput]);

  useEffect(() => {
    const nextQuery = signSearchInput.trim();
    const normalized = signSearchField === "sign_id" ? nextQuery : formatGestureName(nextQuery);
    if (normalized === signSearchTerm) return undefined;

    const timer = setTimeout(() => {
      setSignPage(1);
      setSignSearchTerm(normalized);
    }, 350);

    return () => clearTimeout(timer);
  }, [signSearchInput, signSearchTerm, signSearchField]);

  useEffect(() => {
    fetchSigns(signPage, signPageSize, signSearchTerm, signSearchField);
  }, [fetchSigns, signPage, signPageSize, signSearchTerm, signSearchField]);

  useEffect(() => {
    const fromSigns = signs.flatMap((sign) => collectSignVideoRefIds(sign));
    const fromSeq = sequence.flatMap((ent) => collectSignVideoRefIds({ sign_videos: ent?.sign_video_refs || [] }));
    const ids = [...new Set([...fromSigns, ...fromSeq])];
    if (ids.length) {
      fetchSignVideosByIds(ids);
    }
  }, [signs, sequence, fetchSignVideosByIds]);

  useEffect(() => {
    setSequencePreview(null);
    setSequencePreviewError("");
    setSequencePreviewLoading(false);
  }, [sequence]);

  const fetchSentencesQuery = useCallback(
    async ({ page, pageSize, textContains, untranslatedOnly }) => {
      const base = String(apiBase || "").replace(/\/$/, "");
      const headers = token ? { Authorization: `Bearer ${token}` } : {};

      const params = new URLSearchParams();
      params.append("pagination[page]", String(page));
      params.append("pagination[pageSize]", String(pageSize));
      params.append("populate", "*");
      params.append("sort", "createdAt:desc");
      if (untranslatedOnly) {
        params.append("filters[translations][$null]", "true");
      }
      if (textContains) {
        params.append("filters[text][$containsi]", textContains);
      }

      const fetchPage = async (endpoint) => {
        const resp = await fetch(`${base}${endpoint}?${params.toString()}`, { headers });
        if (!resp.ok) {
          let message = "Ошибка загрузки предложений";
          try {
            const errData = await resp.json();
            message = errData?.error?.message || errData?.message || message;
          } catch {
            const text = await resp.text();
            if (text) message = text;
          }
          throw new Error(message);
        }
        return resp.json();
      };

      let payload;
      try {
        payload = await fetchPage("/api/sentences");
      } catch {
        payload = await fetchPage("/sentences");
      }

      const items = Array.isArray(payload) ? payload : (payload?.data || []);
      const normalized = items.map(normalizeSentenceLocal).filter(Boolean);
      const meta = payload?.meta?.pagination || {};
      return { items: normalized, meta };
    },
    [apiBase, token]
  );

  const loadRandomUntranslated = useCallback(async () => {
    setSentenceOpError("");
    setRandomPreview(null);
    setSentenceOpLoading(true);
    try {
      const first = await fetchSentencesQuery({
        page: 1,
        pageSize: RANDOM_POOL_PAGE_SIZE,
        textContains: "",
        untranslatedOnly: true,
      });

      let pool = first.items.filter(isUntranslated);
      const total = Number(first.meta?.total ?? pool.length);
      const pageCount = Math.max(1, Math.ceil(total / RANDOM_POOL_PAGE_SIZE));

      if (pageCount > 1 && pool.length) {
        const randomPage = 1 + Math.floor(Math.random() * pageCount);
        if (randomPage !== 1) {
          const next = await fetchSentencesQuery({
            page: randomPage,
            pageSize: RANDOM_POOL_PAGE_SIZE,
            textContains: "",
            untranslatedOnly: true,
          });
          pool = next.items.filter(isUntranslated);
        }
      }

      if (!pool.length) {
        setRandomPreview(null);
        setSentenceOpError(
          total === 0
            ? "Нет предложений без перевода."
            : "Не удалось выбрать предложение (проверьте фильтр переводов на стороне API)."
        );
        return;
      }

      const pick = pool[Math.floor(Math.random() * pool.length)];
      setRandomPreview(pick);
    } catch (error) {
      setRandomPreview(null);
      setSentenceOpError(error.message || "Ошибка загрузки предложений");
    } finally {
      setSentenceOpLoading(false);
    }
  }, [fetchSentencesQuery]);

  useEffect(() => {
    if (sentenceMode !== "random") return undefined;
    setRandomPreview(null);
    setSentenceOpError("");
    loadRandomUntranslated();
    return undefined;
  }, [sentenceMode, loadRandomUntranslated]);

  useEffect(() => {
    if (sentenceMode !== "search") return undefined;

    if (!sentenceSearchDebounced) {
      setSearchResults([]);
      setSentenceOpError("");
      return undefined;
    }

    let cancelled = false;
    const run = async () => {
      setSentenceOpLoading(true);
      setSentenceOpError("");
      setSearchResults([]);
      try {
        const { items } = await fetchSentencesQuery({
          page: 1,
          pageSize: SEARCH_PAGE_SIZE,
          textContains: sentenceSearchDebounced,
          untranslatedOnly: true,
        });
        if (cancelled) return;
        const filtered = items.filter(isUntranslated).slice(0, SEARCH_PAGE_SIZE);
        setSearchResults(filtered);
        if (!filtered.length) {
          setSentenceOpError("Ничего не найдено среди предложений без перевода.");
        } else {
          setSentenceOpError("");
        }
      } catch (error) {
        if (!cancelled) {
          setSearchResults([]);
          setSentenceOpError(error.message || "Ошибка поиска");
        }
      } finally {
        if (!cancelled) setSentenceOpLoading(false);
      }
    };

    run();
    return () => {
      cancelled = true;
    };
  }, [sentenceMode, sentenceSearchDebounced, fetchSentencesQuery]);

  const signTotalPages = Math.max(1, signsPagination?.pageCount || 1);

  const selectedKey = sentenceKey(selectedSentence);

  const handlePickSentence = (sentence) => {
    setSelectedSentence(sentence);
    setSequence([]);
    setSaveOkMessage("");
  };

  const handleSentenceModeChange = (next) => {
    if (next === sentenceMode) return;
    setSentenceMode(next);
    setSentenceOpError("");
    setSelectedSentence(null);
    setSequence([]);
    setSaveOkMessage("");
    if (next === "random") {
      setSentenceSearchInput("");
      setSentenceSearchDebounced("");
      setSearchResults([]);
    } else {
      setRandomPreview(null);
    }
  };

  const handleAddSign = (sign) => {
    const id = Number(sign?.id);
    if (!Number.isFinite(id)) return;
    const label = sign?.name || sign?.lemma || `#${id}`;
    const sign_video_refs = normalizeRelationArray(sign?.sign_videos)
      .map((r) => (Number.isFinite(Number(r?.id)) ? { id: Number(r.id) } : null))
      .filter(Boolean);

    setSequence((prev) => [
      ...prev,
      {
        id,
        label,
        sign_id: sign?.sign_id || "",
        sign_video_refs,
      },
    ]);
    setSaveOkMessage("");
  };

  const handleRemoveAt = (index) => {
    setSequence((prev) => prev.filter((_, i) => i !== index));
    setSaveOkMessage("");
  };

  const handleMove = (index, delta) => {
    setSequence((prev) => {
      const next = [...prev];
      const j = index + delta;
      if (j < 0 || j >= next.length) return prev;
      [next[index], next[j]] = [next[j], next[index]];
      return next;
    });
    setSaveOkMessage("");
  };

  const handleStopSequencePreview = () => {
    setSequencePreview(null);
    setSequencePreviewLoading(false);
  };

  const handleSequencePreviewEnded = () => {
    setSequencePreview((prev) => {
      if (!prev?.entries?.length) return prev;
      const nextIdx = prev.index + 1;
      if (nextIdx >= prev.entries.length) return null;
      return { ...prev, index: nextIdx };
    });
  };

  const handleStartSequencePreview = async () => {
    if (!sequence.length) return;

    setSequencePreviewError("");
    setSequencePreview(null);
    setSequencePreviewLoading(true);

    try {
      const allIds = sequence.flatMap((ent) =>
        normalizeRelationArray(ent?.sign_video_refs)
          .map((r) => Number(r?.id))
          .filter((id) => Number.isFinite(id))
      );

      if (!allIds.length) {
        setSequencePreviewError(
          "У жестов нет привязанных sign-video — добавьте жесты с видео или проверьте данные на сервере."
        );
        return;
      }

      const uniqueIds = [...new Set(allIds)];
      const fetchedMap = await fetchSignVideosByIds(uniqueIds, true);
      const videosMap = { ...signVideosById, ...(fetchedMap || {}) };
      const entries = buildSequencePlaybackEntries(sequence, videosMap, apiBase);

      if (!entries.length) {
        setSequencePreviewError("Не удалось собрать видеофайлы для предпросмотра.");
        return;
      }

      setSequencePreview({ entries, index: 0 });
    } catch (error) {
      setSequencePreviewError(error?.message || "Ошибка подготовки предпросмотра");
    } finally {
      setSequencePreviewLoading(false);
    }
  };

  const handleSave = async () => {
    handleStopSequencePreview();
    setSaveOkMessage("");
    const result = await createTranslation({
      sentence: selectedSentence,
      signDocumentIdsOrdered: sequence.map((s) => s.documentId).filter(Boolean),
      language,
      notes,
      reviewed,
    });
    if (!result) return;

    setSaveOkMessage("Перевод сохранён в Strapi.");
    setSequence([]);
    setNotes("");
    setReviewed(false);
    setSelectedSentence(null);

    if (sentenceMode === "random") {
      await loadRandomUntranslated();
    } else if (sentenceSearchDebounced) {
      setSentenceOpLoading(true);
      setSentenceOpError("");
      try {
        const { items } = await fetchSentencesQuery({
          page: 1,
          pageSize: SEARCH_PAGE_SIZE,
          textContains: sentenceSearchDebounced,
          untranslatedOnly: true,
        });
        const filtered = items.filter(isUntranslated).slice(0, SEARCH_PAGE_SIZE);
        setSearchResults(filtered);
        if (!filtered.length) {
          setSentenceOpError("Ничего не найдено среди предложений без перевода.");
        } else {
          setSentenceOpError("");
        }
      } catch (error) {
        setSearchResults([]);
        setSentenceOpError(error.message || "Ошибка обновления поиска");
      } finally {
        setSentenceOpLoading(false);
      }
    }
  };

  return (
    <main className="main-page">
      <div className="main-card">
        <div className="requests-head">
          <h1>
            <i className="fa-solid fa-language" aria-hidden="true"></i> Перевод
          </h1>
          <div className="requests-head-actions" />
        </div>

        <p className="translation-intro">
          Возьмите случайное предложение без перевода или найдите по тексту (до {SEARCH_PAGE_SIZE} вариантов), затем соберите{" "}
          <code className="translation-code">data.signs</code> и сохраните Translation.
        </p>

        <section className="create-sign-panel translation-section">
          <h3>
            <i className="fa-solid fa-quote-left" aria-hidden="true"></i> Предложение
          </h3>

          <div className="translation-mode-switch">
            <button
              type="button"
              className={`aside-link ${sentenceMode === "random" ? "active" : ""}`}
              onClick={() => handleSentenceModeChange("random")}
            >
              <i className="fa-solid fa-shuffle" aria-hidden="true"></i>
              Случайное
            </button>
            <button
              type="button"
              className={`aside-link ${sentenceMode === "search" ? "active" : ""}`}
              onClick={() => handleSentenceModeChange("search")}
            >
              <i className="fa-solid fa-magnifying-glass" aria-hidden="true"></i>
              Поиск
            </button>
          </div>

          {selectedSentence ? (
            <div className="translation-selected-banner">
              <span>
                <strong>Выбрано:</strong> {selectedSentence?.text?.slice(0, 200)}
                {(selectedSentence?.text || "").length > 200 ? "…" : ""}
              </span>
              <span className="sentence-id">#{selectedSentence?.id}</span>
              <button
                type="button"
                className="association-open-btn"
                title="Снять выбор"
                onClick={() => {
                  setSelectedSentence(null);
                  setSequence([]);
                  setSaveOkMessage("");
                }}
              >
                <i className="fa-solid fa-xmark" aria-hidden="true"></i>
              </button>
            </div>
          ) : null}

          {sentenceMode === "random" ? (
            <div className="translation-random-block">
              {sentenceOpLoading ? (
                <p><i className="fa-solid fa-spinner fa-spin" aria-hidden="true"></i> Подбираем предложение…</p>
              ) : null}

              {!sentenceOpLoading && randomPreview ? (
                <div className="translation-random-card">
                  <p className="translation-random-text">{randomPreview.text || `#${randomPreview.id}`}</p>
                  <div className="translation-random-actions">
                    <button className="e-btn" type="button" onClick={() => handlePickSentence(randomPreview)}>
                      <i className="fa-solid fa-check" aria-hidden="true"></i>
                      Выбрать для перевода
                    </button>
                    <button
                      className="e-btn"
                      type="button"
                      onClick={loadRandomUntranslated}
                      disabled={sentenceOpLoading}
                    >
                      <i className="fa-solid fa-rotate-right" aria-hidden="true"></i>
                      Другое
                    </button>
                  </div>
                  <p className="translation-hint">Только предложения без перевода (пустая связь translations).</p>
                </div>
              ) : null}

            </div>
          ) : (
            <div className="translation-search-block">
              <label className="gesture-search translation-sentence-search" htmlFor="translation-sentence-q">
                <i className="fa-solid fa-magnifying-glass" aria-hidden="true"></i>
                <input
                  id="translation-sentence-q"
                  type="search"
                  value={sentenceSearchInput}
                  onChange={(e) => setSentenceSearchInput(e.target.value)}
                  placeholder="Текст предложения…"
                />
              </label>
              <p className="translation-hint">
                Показываем до {SEARCH_PAGE_SIZE} совпадений среди предложений <strong>без перевода</strong>.
              </p>

              {sentenceOpLoading && sentenceSearchDebounced ? (
                <p><i className="fa-solid fa-spinner fa-spin" aria-hidden="true"></i> Поиск…</p>
              ) : null}

              {!sentenceOpLoading && !sentenceSearchDebounced ? (
                <p className="translation-hint">Введите запрос — список появится ниже.</p>
              ) : null}

              {!sentenceOpLoading && sentenceSearchDebounced && searchResults.length ? (
                <div className="requests-list translation-sentence-list">
                  {searchResults.map((s) => {
                    const key = sentenceKey(s);
                    const active = key && key === selectedKey;
                    return (
                      <button
                        type="button"
                        key={key}
                        className={`translation-sentence-row ${active ? "active" : ""}`}
                        onClick={() => handlePickSentence(s)}
                      >
                        <span className="translation-sentence-text">{s?.text || `#${s?.id}`}</span>
                        <span className="sentence-id">id {s?.id}</span>
                      </button>
                    );
                  })}
                </div>
              ) : null}
            </div>
          )}

          {sentenceOpError ? (
            <p className="requests-error">
              <i className="fa-solid fa-circle-exclamation" aria-hidden="true"></i> {sentenceOpError}
            </p>
          ) : null}
        </section>

        <section className="create-sign-panel translation-section">
          <h3>
            <i className="fa-solid fa-list-ol" aria-hidden="true"></i> Последовательность жестов ({sequence.length})
          </h3>
          {!sequence.length ? (
            <p className="translation-hint">Нажмите «В последовательность» у жеста ниже.</p>
          ) : (
            <ol className="translation-sequence-list">
              {sequence.map((item, index) => (
                <li key={`${item.id}-${index}`} className="translation-sequence-item">
                  <GestureVideoPreview
                    sign={{ sign_videos: item.sign_video_refs || [] }}
                    videosById={signVideosById}
                    apiBase={apiBase}
                  />
                  <div className="translation-sequence-main">
                    <span className="translation-sequence-order">{index + 1}.</span>
                    <span className="translation-sequence-label">{item.label}</span>
                    <span className="sentence-id">{item.sign_id ? `sign_id ${item.sign_id}` : `id ${item.id}`}</span>
                  </div>
                  <div className="translation-sequence-actions">
                    <button
                      type="button"
                      className="association-open-btn"
                      title="Выше"
                      onClick={() => handleMove(index, -1)}
                      disabled={index === 0}
                    >
                      <i className="fa-solid fa-arrow-up" aria-hidden="true"></i>
                    </button>
                    <button
                      type="button"
                      className="association-open-btn"
                      title="Ниже"
                      onClick={() => handleMove(index, 1)}
                      disabled={index === sequence.length - 1}
                    >
                      <i className="fa-solid fa-arrow-down" aria-hidden="true"></i>
                    </button>
                    <button
                      type="button"
                      className="danger-btn"
                      title="Убрать"
                      onClick={() => handleRemoveAt(index)}
                    >
                      <i className="fa-solid fa-times" aria-hidden="true"></i>
                    </button>
                  </div>
                </li>
              ))}
            </ol>
          )}
        </section>

        <section className="create-sign-panel translation-section">
          <h3>
            <i className="fa-solid fa-hands" aria-hidden="true"></i> Поиск жестов
          </h3>

          <div className="requests-head-actions translation-sign-head">
            <label className="gesture-search" htmlFor="translation-signs-search">
              <i className="fa-solid fa-magnifying-glass" aria-hidden="true"></i>
              <select
                value={signSearchField}
                onChange={(e) => {
                  setSignSearchField(e.target.value === "sign_id" ? "sign_id" : "name");
                  setSignPage(1);
                }}
                aria-label="Поле поиска"
              >
                <option value="name">name</option>
                <option value="sign_id">sign_id</option>
              </select>
              <input
                id="translation-signs-search"
                type="text"
                value={signSearchInput}
                onChange={(e) => setSignSearchInput(e.target.value)}
                placeholder={`Поиск по ${signSearchField}`}
              />
            </label>
            <button
              className="e-btn"
              type="button"
              onClick={() => fetchSigns(signPage, signPageSize, signSearchTerm, signSearchField)}
            >
              <i className="fa-solid fa-rotate-right" aria-hidden="true"></i>
            </button>
          </div>

          {!signsLoading ? (
            <p>
              <i className="fa-solid fa-layer-group" aria-hidden="true"></i>
              Всего: {signsPagination?.total || 0} · стр. {signsPagination?.page || signPage} / {signTotalPages}
            </p>
          ) : null}

          {signsLoading ? <p><i className="fa-solid fa-spinner fa-spin" aria-hidden="true"></i> Жесты…</p> : null}
          {signsError ? (
            <p className="requests-error"><i className="fa-solid fa-circle-exclamation" aria-hidden="true"></i> {signsError}</p>
          ) : null}

          {signs.length ? (
            <div className="requests-list">
              {signs.map((sign, idx) => (
                <article className="requests-item translation-sign-row" key={sign?.id || `sig-${idx}`}>
                  <div className="translation-sign-row-inner">
                    <GestureVideoPreview sign={sign} videosById={signVideosById} apiBase={apiBase} />
                    <div className="translation-sign-row-body">
                      <div className="requests-item-top">
                        <div className="requests-item-lemma">
                          <i className="fa-solid fa-hand" aria-hidden="true"></i>
                          <span>{sign?.name || sign?.lemma || `Жест #${sign?.id}`}</span>
                        </div>
                        <button className="e-btn" type="button" onClick={() => handleAddSign(sign)}>
                          <i className="fa-solid fa-plus" aria-hidden="true"></i>
                          В последовательность
                        </button>
                      </div>
                      <div className="sentence-meta-inline">
                        <span className="sentence-id">id {sign?.id}</span>
                        {sign?.sign_id ? <span className="sentence-id">{String(sign.sign_id)}</span> : null}
                      </div>
                    </div>
                  </div>
                </article>
              ))}
            </div>
          ) : null}

          {!signsLoading && signs.length ? (
            <Pagination
              currentPage={signPage}
              totalPages={signTotalPages}
              onPageChange={setSignPage}
            />
          ) : null}
        </section>

        <section className="create-sign-panel translation-section">
          <h3>
            <i className="fa-solid fa-circle-play" aria-hidden="true"></i> Предпросмотр всей последовательности
          </h3>
          <p className="translation-hint">
            Видео воспроизводятся по порядку (как в переводе на странице «Предложения»): каждый файл sign-video подряд, затем переход к следующему жесту.
          </p>

          <div className="translation-preview-actions">
            <button
              type="button"
              className="e-btn"
              disabled={!sequence.length || sequencePreviewLoading}
              onClick={sequencePreview?.entries?.length ? handleStopSequencePreview : handleStartSequencePreview}
            >
              <i className={`fa-solid ${sequencePreview?.entries?.length ? "fa-stop" : "fa-play"}`} aria-hidden="true"></i>
              {sequencePreviewLoading
                ? "Загрузка…"
                : sequencePreview?.entries?.length
                  ? "Стоп"
                  : "Проиграть всю последовательность"}
            </button>
          </div>

          {sequencePreviewLoading ? (
            <p><i className="fa-solid fa-spinner fa-spin" aria-hidden="true"></i> Подготавливаем видео…</p>
          ) : null}

          {sequencePreviewError ? (
            <p className="requests-error">
              <i className="fa-solid fa-circle-exclamation" aria-hidden="true"></i> {sequencePreviewError}
            </p>
          ) : null}

          {sequencePreview?.entries?.length ? (
            <div className="sentence-playback translation-sequence-preview-playback">
              <video
                key={sequencePreview.entries[sequencePreview.index]?.url || sequencePreview.index}
                className="sign-video-player"
                controls
                autoPlay
                preload="metadata"
                playsInline
                src={sequencePreview.entries[sequencePreview.index].url}
                onEnded={handleSequencePreviewEnded}
                onError={handleSequencePreviewEnded}
              >
                <source
                  src={sequencePreview.entries[sequencePreview.index].url}
                  type={sequencePreview.entries[sequencePreview.index].mime || "video/mp4"}
                />
                Ваш браузер не поддерживает тег video.
              </video>
              <p className="sentence-playback-meta">
                Видео {sequencePreview.index + 1} из {sequencePreview.entries.length}
                {" · "}
                {sequencePreview.entries[sequencePreview.index]?.signLabel}
              </p>
            </div>
          ) : null}

          {!sequence.length ? (
            <p className="translation-hint">Сначала соберите последовательность жестов.</p>
          ) : null}
        </section>

        <section className="create-sign-panel translation-section">
          <h3>
            <i className="fa-solid fa-paper-plane" aria-hidden="true"></i> Сохранение в Strapi
          </h3>

          <div className="translation-meta-grid">
            <label>
              <span>language (enumeration)</span>
              <input
                type="text"
                value={language}
                onChange={(e) => setLanguage(e.target.value)}
                placeholder="как в админке Strapi"
              />
            </label>
            <label className="translation-checkbox-row">
              <input type="checkbox" checked={reviewed} onChange={(e) => setReviewed(e.target.checked)} />
              <span>reviewed</span>
            </label>
            <label className="translation-notes">
              <span>notes</span>
              <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={3} placeholder="Необязательно" />
            </label>
          </div>

          {translationCreateError ? (
            <p className="requests-error"><i className="fa-solid fa-circle-exclamation" aria-hidden="true"></i> {translationCreateError}</p>
          ) : null}
          {saveOkMessage ? (
            <p><i className="fa-solid fa-check" aria-hidden="true"></i> {saveOkMessage}</p>
          ) : null}

          <button
            className="e-btn"
            type="button"
            disabled={translationCreateLoading || !selectedSentence}
            onClick={handleSave}
          >
            <i className="fa-solid fa-cloud-arrow-up" aria-hidden="true"></i>
            {translationCreateLoading ? "Отправка…" : "Сохранить перевод"}
          </button>
          {!selectedSentence ? (
            <p className="translation-hint">Сначала выберите предложение (случайное или из поиска).</p>
          ) : null}
        </section>
      </div>
    </main>
  );
};

export default TranslationPage;
